import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import SendInformationCard from "./SendInformationCard";
import RecipientListCard from "./RecipientListCard";
import PreviewSection from "./PreviewSection";
import ActionCard from "./ActionCard";
import EnvelopeInformationCard from "./EnvelopeInformationCard";
import SendEmailConfirmationModal from "./SendEmailConfirmationModal";
import RecipientListCreatedModal from "./RecipientListCreatedModal";
import LegacyButton from "../../components/Atomos/Buttons/LegacyButton";
import UpdateIcon from "../../components/Atomos/Icons/UpdateIcon";
import {
  SendEmailFormData,
  ActionFormData,
  EmailTemplate,
  Operator,
  RecipientConditionGroup,
  RecipientSelection,
} from "./types";
import { getEmailTemplateById, getEmailTemplates } from "../../Services/EmailTemplate";
import { IEmailTemplate } from "../../interfaces/models/IEmailTemplate";
import {
  buildTemplateCreatedDateFilter,
  createDefaultTemplateDateFilterLast6Months,
  parseStoredTemplateDateFilter,
} from "./templateDateFilter";
import { useLocalPreferences } from "../../hooks/useLocalPreferences";
import {
  getEmailDistributionListById,
  createEmailDistributionList,
  associateRecipientListWithTemplate,
} from "../../Services/EmailDistributionList";
import { calculateRecipients } from "../../Services/EmailCalculateRecipients";
import { createEmailSend } from "../../Services/EmailSend";
import { getWSGFirmIds } from "../../Services/Firm";
import { fetchPersonByFirmIds } from "../../Services/Person";
import {
  IEmailDistributionList,
  IEmailRecipientFilterGroup,
} from "../../interfaces/models/IEmailDistributionList";
import { useAuth } from "../../store/AuthContext";
import { toastError, toastWarn } from "../../utils/toast";
import {
  findUnsupportedEmailPlaceholders,
  unsupportedPlaceholderWarningMessage,
} from "../../utils/emailTemplatePlaceholders";
import {
  buildRecipientFilterFromSelection,
  cloneRecipientSelection,
  hasRecipientListFilterEdits,
  parseRecipientFilterForEditor,
} from "./recipientSelectionUtils";
import {
  getBuiltinEmailDistributionList,
  isBuiltinRecipientListId,
  isSkipStep2RecipientListId,
} from "./builtinRecipientLists";
import {
  buildPersonIdInFilter,
  mergeRecipientFiltersOr,
} from "./sendEmailRecipientFilters";
import { WSG_STAFF_FIRM_IDS } from "../../utils/wsgStaffFirms";

const ArrowRightIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-4 h-4"
  >
    <path
      d="M9 5L16 12L9 19"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DEFAULT_FORM_DATA: SendEmailFormData = {
  ...createDefaultTemplateDateFilterLast6Months(),
  emailTemplateId: "",
  recipientListId: "",
  testListMemberIds: [],
};

/** localStorage key under wsg_local_prefs tableFilters (not Cosmos user prefs). */
const SEND_EMAIL_TEMPLATE_FILTERS_KEY = "send-email-template-filters";

type TestListMemberOption = {
  id: string;
  name: string;
  email: string;
  firmId: string;
  firmName: string;
  location: string;
  region: string;
};

function formatPersonRegionFromRecord (p: Record<string, unknown>): string {
  const r = p.region;
  if (Array.isArray(r)) {
    return r
      .map((x) => String(x ?? "").trim())
      .filter(Boolean)
      .join(", ");
  }
  if (typeof r === "string") {
    return r.trim();
  }
  return "";
}

function mapPersonToTestListOption (
  p: Record<string, unknown>,
): TestListMemberOption | null {
  const id = String(p.id ?? "").trim();
  if (!id || p.isDeleted) {
    return null;
  }
  const email = String(p.firmEmail ?? p.email ?? "").trim();
  if (!email) {
    return null;
  }
  const name =
    [p.name, p.middleInitial, p.lastName]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim() || "Member";
  return {
    id,
    name,
    email,
    firmId: String(p.firm ?? "").trim(),
    firmName: String(p.firmName ?? "").trim(),
    location: String(
      p.country ?? p.mainOfficeCountry ?? p.Country ?? "",
    ).trim(),
    region: formatPersonRegionFromRecord(p),
  };
}

const DEFAULT_ACTION_DATA: ActionFormData = {
  actionType: "schedule",
  scheduledDate: "",
  scheduledTime: "",
  timeZone: "regional",
};

const EASTERN_TIME_ZONE = "America/New_York";

type CurrentStep = 1 | 3;
type Step2Selection = RecipientSelection;

const EMAIL_TEMPLATE_LIST_FIELDS = [
  "id",
  "emailNumber",
  "title",
  "createdDate",
];

const EMAIL_TEMPLATE_DETAIL_FIELDS = [
  "id",
  "emailNumber",
  "title",
  "template",
  "createdDate",
  "subject",
  "sender",
  "fromEmail",
  "category",
  "emailType",
  "emailEventId",
  "attachment",
];

const areStringArraysEqual = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => value === right[index]);
};

const getTemplateYear = (createdDate?: number): string => {
  if (!createdDate) {
    return "";
  }
  const timestampMs = createdDate > 9999999999 ? createdDate : createdDate * 1000;
  return String(new Date(timestampMs).getFullYear());
};

const mapTemplateToOption = (template: IEmailTemplate): EmailTemplate => ({
  id: template.id || "",
  emailNumber: template.emailNumber || undefined,
  name: `${template.emailNumber} - ${template.title}` || "Untitled template",
  title: template.title || "",
  year: getTemplateYear(template.createdDate),
  htmlContent: template.template || "",
  subject: template.subject || "",
  sender: template.sender || "",
  fromEmail: template.fromEmail || "",
  category: template.category || "",
  emailType: template.emailType || "",
  emailEventId: template.emailEventId || "",
  attachment: template.attachment || "",
});

const sanitizeTemplateTitleForListName = (title: string): string => {
  const cleanedTitle = title
    .replace(/['&]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleanedTitle) {
    return "Untitled Template";
  }
  if (cleanedTitle.length <= 24) {
    return cleanedTitle;
  }

  const truncated = cleanedTitle.slice(0, 22).trim();
  const lastSpace = truncated.lastIndexOf(" ");
  if (lastSpace > 0) {
    return truncated.slice(0, lastSpace).trim();
  }
  return truncated;
};

const pad2 = (value: number): string => String(value).padStart(2, "0");

const buildDistributionListName = (
  templateName: string,
  date: Date = new Date(),
): string => {
  const year = date.getFullYear();
  const month = pad2(date.getMonth() + 1);
  const day = pad2(date.getDate());
  const hour = pad2(date.getHours());
  const minute = pad2(date.getMinutes());
  const cleanTemplateName = sanitizeTemplateTitleForListName(templateName);

  return `${year} ${month}.${day} ${cleanTemplateName} ${hour}:${minute}`;
};

const parseScheduledDateTimeToSeconds = (
  scheduledDate: string,
  scheduledTime: string,
): number | null => {
  const dateMatch = scheduledDate
    .trim()
    .match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const timeMatch = scheduledTime
    .trim()
    .match(/^([01]\d|2[0-3]):([0-5]\d)$/);

  if (!dateMatch || !timeMatch) {
    return null;
  }

  const month = Number(dateMatch[1]);
  const day = Number(dateMatch[2]);
  const year = Number(dateMatch[3]);
  const hour = Number(timeMatch[1]);
  const minute = Number(timeMatch[2]);

  const easternDateTime = `${year}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}:00`;
  const parsedDateTime = fromZonedTime(easternDateTime, EASTERN_TIME_ZONE);
  if (Number.isNaN(parsedDateTime.getTime())) {
    return null;
  }

  const parsedEasternDateTime = toZonedTime(parsedDateTime, EASTERN_TIME_ZONE);
  const isSameDate =
    parsedEasternDateTime.getFullYear() === year &&
    parsedEasternDateTime.getMonth() === month - 1 &&
    parsedEasternDateTime.getDate() === day &&
    parsedEasternDateTime.getHours() === hour &&
    parsedEasternDateTime.getMinutes() === minute;
  if (!isSameDate) {
    return null;
  }

  return Math.floor(parsedDateTime.getTime() / 1000);
};

const getEmailSendErrorMessage = (error: unknown): string => {
  if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "data" in error.response
  ) {
    const responseData = error.response.data;
    if (typeof responseData === "string" && responseData.trim().length > 0) {
      return responseData;
    }
    if (
      responseData &&
      typeof responseData === "object" &&
      "message" in responseData &&
      typeof responseData.message === "string" &&
      responseData.message.trim().length > 0
    ) {
      return responseData.message;
    }
  }

  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string" &&
    error.message.trim().length > 0
  ) {
    return error.message;
  }

  return "Unable to submit email send. Please try again.";
};

export default function SendEmail (): React.ReactElement {
  const navigate = useNavigate();
  const { loggedPerson, tokenDecoded } = useAuth();
  const { getTableFilters, setTableFilters, userId } = useLocalPreferences();
  const [formData, setFormData] = useState<SendEmailFormData>(DEFAULT_FORM_DATA);
  const [templateDateFilterReady, setTemplateDateFilterReady] = useState(false);
  const [actionData, setActionData] = useState<ActionFormData>(DEFAULT_ACTION_DATA);
  const [isCreatingNewList, setIsCreatingNewList] = useState(false);
  const [currentStep, setCurrentStep] = useState<CurrentStep>(1);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [excludedEmails, setExcludedEmails] = useState<string[]>([]);
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false);
  const [isSelectedTemplateLoading, setIsSelectedTemplateLoading] =
    useState(false);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const loadedTemplateDetailIdsRef = useRef(new Set<string>());
  const placeholderWarnedForIdRef = useRef<string | null>(null);
  const [selectedDistributionList, setSelectedDistributionList] =
    useState<IEmailDistributionList | null>(null);
  const [isSelectedDistributionListLoading, setIsSelectedDistributionListLoading] =
    useState(false);
  const [testListMemberOptions, setTestListMemberOptions] = useState<
    TestListMemberOption[]
  >([]);
  const [isTestListMembersLoading, setIsTestListMembersLoading] =
    useState(false);
  const [previewRecipients, setPreviewRecipients] = useState<
    {
      id: string;
      contact: string;
      email: string;
      firmName: string;
      location: string;
      region: string;
    }[]
  >([]);
  const [previewRecipientsCount, setPreviewRecipientsCount] = useState(0);
  const [previewServerPaginated, setPreviewServerPaginated] = useState(false);
  const [previewRecipientFilter, setPreviewRecipientFilter] =
    useState<IEmailRecipientFilterGroup | null>(null);
  const previewRecipientSortRef = useRef<{
    field: string;
    order: "asc" | "desc";
  }>({ field: "lastName", order: "asc" });
  const [lastPreviewedStep2Selection, setLastPreviewedStep2Selection] =
    useState<Step2Selection | null>(null);
  const [lastPreviewedExcludedEmails, setLastPreviewedExcludedEmails] = useState<
    string[]
  >([]);
  const [isPreviewRecipientsLoading, setIsPreviewRecipientsLoading] =
    useState(false);
  const [openRecipientListSignal, setOpenRecipientListSignal] = useState(0);
  const [isPersistingDistributionList, setIsPersistingDistributionList] =
    useState(false);
  const [showListCreatedModal, setShowListCreatedModal] = useState(false);
  const [createdListName, setCreatedListName] = useState("");
  const [isSubmittingEmailSend, setIsSubmittingEmailSend] = useState(false);
  const [isEnvelopeEditing, setIsEnvelopeEditing] = useState(false);
  const preservePreviewOnNextListSelectionRef = useRef(false);
  const lastAutoPreviewedListIdRef = useRef("");
  const previewRequestIdRef = useRef(0);
  const excludedEmailsRef = useRef<string[]>([]);
  const skipNextExcludedEmailsInvalidationRef = useRef(false);

  useEffect(() => {
    excludedEmailsRef.current = excludedEmails;
  }, [excludedEmails]);

  useEffect(() => {
    if (!userId || templateDateFilterReady) {
      return;
    }
    const stored = getTableFilters(SEND_EMAIL_TEMPLATE_FILTERS_KEY);
    const restored = parseStoredTemplateDateFilter(
      stored.inputFilters?.templateDateFilter,
    );
    if (restored) {
      setFormData((prev) => ({
        ...prev,
        templateDatePreset: restored.templateDatePreset,
        templateDateFrom: restored.templateDateFrom,
        templateDateTo: restored.templateDateTo,
      }));
    }
    setTemplateDateFilterReady(true);
  }, [userId, getTableFilters, templateDateFilterReady]);

  useEffect(() => {
    if (!userId || !templateDateFilterReady) {
      return;
    }
    setTableFilters(SEND_EMAIL_TEMPLATE_FILTERS_KEY, {
      inputFilters: {
        templateDateFilter: {
          templateDatePreset: formData.templateDatePreset,
          templateDateFrom: formData.templateDateFrom,
          templateDateTo: formData.templateDateTo,
        },
      },
    });
  }, [
    userId,
    templateDateFilterReady,
    setTableFilters,
    formData.templateDatePreset,
    formData.templateDateFrom,
    formData.templateDateTo,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsTestListMembersLoading(true);
      try {
        const firmIds = await getWSGFirmIds();
        const memberFirmIds = firmIds.filter((id) => !WSG_STAFF_FIRM_IDS.has(id));
        const firmIdsToQuery = memberFirmIds.length > 0 ? memberFirmIds : firmIds;
        if (!firmIdsToQuery.length) {
          if (!cancelled) {
            setTestListMemberOptions([]);
          }
          return;
        }
        const people = await fetchPersonByFirmIds(firmIdsToQuery);
        if (cancelled) {
          return;
        }
        const opts = (people || [])
          .map((p: Record<string, unknown>) =>
            mapPersonToTestListOption(p),
          )
          .filter((o): o is TestListMemberOption => o !== null)
          .sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
          );
        setTestListMemberOptions(opts);
      } catch {
        if (!cancelled) {
          setTestListMemberOptions([]);
        }
      } finally {
        if (!cancelled) {
          setIsTestListMembersLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const recipientListId = formData.recipientListId;

    if (!recipientListId) {
      setSelectedDistributionList(null);
      setIsSelectedDistributionListLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const builtinList = getBuiltinEmailDistributionList(recipientListId);
    if (builtinList) {
      setSelectedDistributionList(builtinList);
      setIsSelectedDistributionListLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setSelectedDistributionList(null);
    setIsSelectedDistributionListLoading(true);

    (async () => {
      const list = await getEmailDistributionListById(recipientListId);
      if (cancelled) {
        return;
      }
      setSelectedDistributionList(list);
      setIsSelectedDistributionListLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [formData.recipientListId]);

  useEffect(() => {
    let mounted = true;

    const fetchEmailTemplates = async () => {
      setIsTemplatesLoading(true);
      setEmailTemplates([]);
      loadedTemplateDetailIdsRef.current.clear();
      placeholderWarnedForIdRef.current = null;

      const filter = buildTemplateCreatedDateFilter(
        formData.templateDatePreset,
        formData.templateDateFrom,
        formData.templateDateTo,
      );
      const templates = await getEmailTemplates({
        filter,
        fields: EMAIL_TEMPLATE_LIST_FIELDS,
        sort: { field: "createdDate", order: "desc" },
      });

      if (!mounted) {
        return;
      }

      const options = templates
        .filter((template) => !!template.id)
        .map(mapTemplateToOption);
      setEmailTemplates(options);
      setIsTemplatesLoading(false);
    };

    fetchEmailTemplates();

    return () => {
      mounted = false;
    };
  }, [
    formData.templateDatePreset,
    formData.templateDateFrom,
    formData.templateDateTo,
  ]);

  useEffect(() => {
    const templateId = formData.emailTemplateId;
    if (!templateId) {
      setIsSelectedTemplateLoading(false);
      placeholderWarnedForIdRef.current = null;
      return;
    }

    if (loadedTemplateDetailIdsRef.current.has(templateId)) {
      setIsSelectedTemplateLoading(false);
      return;
    }

    let cancelled = false;
    setIsSelectedTemplateLoading(true);

    void (async () => {
      const detail = await getEmailTemplateById(
        templateId,
        EMAIL_TEMPLATE_DETAIL_FIELDS,
      );
      if (cancelled) {
        return;
      }

      if (detail?.id) {
        loadedTemplateDetailIdsRef.current.add(detail.id);
        const mapped = mapTemplateToOption(detail);
        setEmailTemplates((prev) =>
          prev.map((template) =>
            template.id === detail.id ? { ...template, ...mapped } : template,
          ),
        );

        const html = String(detail.template ?? "").trim();
        if (
          html &&
          placeholderWarnedForIdRef.current !== detail.id
        ) {
          const unsupported = findUnsupportedEmailPlaceholders(html);
          if (unsupported.length > 0) {
            placeholderWarnedForIdRef.current = detail.id;
            toastWarn(unsupportedPlaceholderWarningMessage(unsupported), {
              autoClose: 8000,
            });
          }
        }
      }
      setIsSelectedTemplateLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [formData.emailTemplateId]);

  const handleFormChange = useCallback((updates: Partial<SendEmailFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
    if (updates.recipientListId) {
      setIsCreatingNewList(false);
    }
  }, []);

  const handleActionChange = useCallback((updates: Partial<ActionFormData>) => {
    setActionData((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleRecipientListTemplateLinked = useCallback(
    (listId: string, emailTemplateIds: string[]) => {
      setSelectedDistributionList((prev) =>
        prev?.id === listId ? { ...prev, emailTemplateIds } : prev,
      );
    },
    [],
  );

  const handleCreateNewList = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      recipientListId: "",
    }));
    if (excludedEmailsRef.current.length > 0) {
      skipNextExcludedEmailsInvalidationRef.current = true;
      setExcludedEmails([]);
    }
    setIsCreatingNewList(true);
  }, []);

  const createDistributionListAndShowConfirmation = useCallback(
    async (recipientFilter: IEmailRecipientFilterGroup) => {
      setIsPersistingDistributionList(true);

      try {
        const selectedTemplate = emailTemplates.find(
          (template) => template.id === formData.emailTemplateId,
        );
        const selectedTemplateName = selectedTemplate?.name || "Untitled Template";
        const selectedTemplateEmailNumber = String(
          selectedTemplate?.emailNumber ?? "",
        ).trim();

        const generatedName = buildDistributionListName(selectedTemplateName);
        const createdList = await createEmailDistributionList({
          name: generatedName,
          filter: recipientFilter,
          isTestList: false,
          createdDate: Date.now(),
          ...(selectedTemplateEmailNumber
            ? { emailTemplateIds: [selectedTemplateEmailNumber] }
            : {}),
        });

        if (!createdList) {
          toastError("Unable to create recipient list. Please try again.");
          return;
        }

        setSelectedDistributionList(createdList);

        if (createdList.id) {
          preservePreviewOnNextListSelectionRef.current = true;
          setFormData((prev) => ({
            ...prev,
            recipientListId: createdList.id || "",
          }));
        }

        setIsCreatingNewList(false);
        setCreatedListName(createdList.name || generatedName);
        setShowListCreatedModal(true);
      } finally {
        setIsPersistingDistributionList(false);
      }
    },
    [emailTemplates, formData.emailTemplateId],
  );

  const handleProceedToStep3 = useCallback(
    async (selection: Step2Selection) => {
      const recipientFilter = buildRecipientFilterFromSelection(
        selection,
        excludedEmails,
      );
      if (!recipientFilter) {
        return;
      }

      const selectedListId = formData.recipientListId;
      const selectedList =
        selectedListId && selectedDistributionList?.id === selectedListId
          ? selectedDistributionList
          : null;

      if (selectedList && !isCreatingNewList) {
        const hasChanges = hasRecipientListFilterEdits(
          selection,
          excludedEmails,
          selectedList.filter,
        );

        if (!hasChanges) {
          const selectedTemplate = emailTemplates.find(
            (template) => template.id === formData.emailTemplateId,
          );
          const selectedTemplateEmailNumber = String(
            selectedTemplate?.emailNumber ?? "",
          ).trim();

          if (
            selectedTemplateEmailNumber &&
            selectedList.id &&
            !isBuiltinRecipientListId(selectedList.id)
          ) {
            const updated = await associateRecipientListWithTemplate(
              selectedList.id,
              selectedTemplateEmailNumber,
            );
            if (updated) {
              setSelectedDistributionList(updated);
            }
          }

          setCurrentStep(3);
          return;
        }

        await createDistributionListAndShowConfirmation(recipientFilter);
        return;
      }

      if (isCreatingNewList) {
        await createDistributionListAndShowConfirmation(recipientFilter);
        return;
      }

      setCurrentStep(3);
    },
    [
      formData.recipientListId,
      formData.emailTemplateId,
      emailTemplates,
      selectedDistributionList,
      excludedEmails,
      isCreatingNewList,
      createDistributionListAndShowConfirmation,
    ],
  );

  const handleContinueAfterListCreated = useCallback(() => {
    setShowListCreatedModal(false);
    setCurrentStep(3);
  }, []);

  const handleBackToStep1 = useCallback(() => {
    setIsEnvelopeEditing(false);
    setCurrentStep(1);
  }, []);

  /** Step 2 list builder is skipped for Test List only and built-in lists (e.g. All active members). */
  const handleProceedToStep3FromTestList = useCallback(() => {
    if (!formData.emailTemplateId) {
      return;
    }
    if (formData.testListMemberIds.length === 0) {
      return;
    }
    if (formData.recipientListId || isCreatingNewList) {
      return;
    }
    setCurrentStep(3);
  }, [
    formData.emailTemplateId,
    formData.testListMemberIds.length,
    formData.recipientListId,
    isCreatingNewList,
  ]);

  const handleProceedToStep3FromBuiltinList = useCallback(() => {
    if (!formData.emailTemplateId) {
      return;
    }
    if (!isSkipStep2RecipientListId(formData.recipientListId)) {
      return;
    }
    if (isCreatingNewList) {
      return;
    }
    setCurrentStep(3);
  }, [formData.emailTemplateId, formData.recipientListId, isCreatingNewList]);

  const handleSubmitClick = useCallback(() => {
    setShowConfirmationModal(true);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    if (isSubmittingEmailSend) {
      return;
    }
    setShowConfirmationModal(false);
  }, [isSubmittingEmailSend]);

  const handleConfirmSend = useCallback(async () => {
    if (isSubmittingEmailSend) {
      return;
    }

    if (!formData.emailTemplateId) {
      toastError("Email template is required.");
      return;
    }

    const selectedListId = formData.recipientListId;
    const selectedList =
      selectedListId && selectedDistributionList?.id === selectedListId
        ? selectedDistributionList
        : null;

    const distributionFilter = selectedList?.filter;
    const testFilter = buildPersonIdInFilter(formData.testListMemberIds);
    const recipientFilter = mergeRecipientFiltersOr(distributionFilter, testFilter);

    if (!recipientFilter) {
      toastError(
        "Please select at least one recipient: a recipient list or one or more Test List members.",
      );
      return;
    }

    const sendType = actionData.actionType === "send" ? "sendNow" : "scheduled";
    const timezoneType = actionData.timeZone === "regional" ? "RegionalTime" : "EST";

    let scheduledDateTime: number | undefined;
    if (sendType === "scheduled") {
      scheduledDateTime = parseScheduledDateTimeToSeconds(
        actionData.scheduledDate,
        actionData.scheduledTime,
      ) || undefined;

      if (!scheduledDateTime) {
        toastError("Please provide a valid scheduled date and time.");
        return;
      }
    }

    const createdBy = loggedPerson?.id || tokenDecoded?.oid || "";
    if (!createdBy) {
      toastError("Unable to determine current user.");
      return;
    }
    const createdByName = [loggedPerson?.name, loggedPerson?.lastName]
      .map((value) => (value || "").trim())
      .filter(Boolean)
      .join(" ")
      .trim();

    setIsSubmittingEmailSend(true);
    try {
      const createdEmailSend = await createEmailSend({
        templateId: formData.emailTemplateId,
        recipientFilter,
        sendType,
        timezoneType,
        createdBy,
        ...(createdByName ? { createdByName } : {}),
        ...(selectedList?.id && !isBuiltinRecipientListId(selectedList.id)
          ? { distributionListId: selectedList.id }
          : {}),
        ...(selectedList?.name ? { distributionListName: selectedList.name } : {}),
        ...(scheduledDateTime ? { scheduledDateTime } : {}),
        isTestList:
          selectedList?.isTestList === true ||
          (!selectedList && formData.testListMemberIds.length > 0),
      });

      setShowConfirmationModal(false);

      if (sendType === "sendNow" && createdEmailSend.status === "failed") {
        toastError(
          createdEmailSend.errorDetails ||
          "The email couldn't be sent. Please review the error details."
        );
        return;
      }

      navigate("/send-email/scheduled-emails");
      //     : "Email queued successfully!",
      // );
    } catch (error) {
      toastError(getEmailSendErrorMessage(error));
    } finally {
      setIsSubmittingEmailSend(false);
    }
  }, [
    isSubmittingEmailSend,
    formData.emailTemplateId,
    formData.recipientListId,
    formData.testListMemberIds,
    selectedDistributionList,
    actionData.actionType,
    actionData.timeZone,
    actionData.scheduledDate,
    actionData.scheduledTime,
    loggedPerson?.id,
    loggedPerson?.name,
    loggedPerson?.lastName,
    tokenDecoded?.oid,
    navigate,
  ]);

  const handleExcludeEmails = useCallback((emails: string[]) => {
    setExcludedEmails((prev) => {
      const existingEmails = new Set(
        prev.map((email) => email.trim().toLowerCase()),
      );
      const nextEmails = [...prev];

      emails.forEach((email) => {
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail || existingEmails.has(normalizedEmail)) {
          return;
        }
        existingEmails.add(normalizedEmail);
        nextEmails.push(normalizedEmail);
      });

      return nextEmails.length === prev.length ? prev : nextEmails;
    });
  }, []);

  const handleExcludeEmail = useCallback((email: string) => {
    handleExcludeEmails([email]);
  }, [handleExcludeEmails]);

  const handleRemoveExclusion = useCallback((email: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      return;
    }

    setExcludedEmails((prev) =>
      prev.filter((e) => e.trim().toLowerCase() !== normalizedEmail)
    );
  }, []);

  const mapCalculatedRecipientsToRows = useCallback(
    (recipients: Awaited<ReturnType<typeof calculateRecipients>>["recipients"]) =>
      recipients.map((recipient, index) => ({
        id: recipient.id || `recipient-${index}`,
        contact: recipient.name || "",
        email: recipient.email || "",
        firmId: recipient.firmId || "",
        firmName: recipient.firm || "",
        location: recipient.country || "",
        region: Array.isArray(recipient.region)
          ? recipient.region.join(", ")
          : "",
      })),
    [],
  );

  const mapPreviewSortFieldToServer = useCallback((field: string) => {
    if (field === "contact") {
      return "lastName";
    }
    if (field === "email") {
      return "email";
    }
    if (field === "firmName" || field === "location" || field === "region") {
      return "lastName";
    }
    return field;
  }, []);

  const loadPreviewRecipients = useCallback(
    async (
      recipientFilter: IEmailRecipientFilterGroup,
      pageIndex: number,
      pageSize: number,
      sortField = previewRecipientSortRef.current.field,
      sortOrder = previewRecipientSortRef.current.order,
      requestId?: number,
    ) => {
      const response = await calculateRecipients({
        recipientFilter,
        templateId: formData.emailTemplateId || undefined,
        sortField: mapPreviewSortFieldToServer(sortField),
        sortOrder,
        pageIndex,
        pageSize,
      });

      if (requestId !== undefined && requestId !== previewRequestIdRef.current) {
        return null;
      }

      return response;
    },
    [formData.emailTemplateId, mapPreviewSortFieldToServer],
  );

  const handlePreviewRecipientList = useCallback(
    async (
      selection: Step2Selection,
      previewExcludedEmails?: string[],
      testMemberIds?: string[],
    ) => {
      const requestId = previewRequestIdRef.current + 1;
      previewRequestIdRef.current = requestId;
      const effectiveExcludedEmails =
        previewExcludedEmails ?? excludedEmailsRef.current;
      const baseFilter = buildRecipientFilterFromSelection(
        selection,
        effectiveExcludedEmails,
      );
      const testFilter = buildPersonIdInFilter(
        testMemberIds ?? formData.testListMemberIds,
      );
      const recipientFilter = mergeRecipientFiltersOr(baseFilter, testFilter);
      if (!recipientFilter) {
        if (requestId !== previewRequestIdRef.current) {
          return;
        }
        setPreviewRecipients([]);
        setPreviewRecipientsCount(0);
        setPreviewServerPaginated(false);
        setPreviewRecipientFilter(null);
        setLastPreviewedStep2Selection(null);
        setLastPreviewedExcludedEmails([]);
        setOpenRecipientListSignal((prev) => prev + 1);
        return;
      }

      setIsPreviewRecipientsLoading(true);
      try {
        const response = await loadPreviewRecipients(
          recipientFilter,
          0,
          50,
          "lastName",
          "asc",
          requestId,
        );
        if (!response || requestId !== previewRequestIdRef.current) {
          return;
        }

        setPreviewRecipients(mapCalculatedRecipientsToRows(response.recipients));
        setPreviewRecipientsCount(
          response.pagination?.totalCount ??
          response.count ??
          response.recipients.length,
        );
        setPreviewServerPaginated(response.serverPaginated === true);
        setPreviewRecipientFilter(recipientFilter);
        previewRecipientSortRef.current = { field: "lastName", order: "asc" };
        setLastPreviewedStep2Selection(cloneRecipientSelection(selection));
        setLastPreviewedExcludedEmails([...effectiveExcludedEmails]);
      } catch {
        if (requestId !== previewRequestIdRef.current) {
          return;
        }
        setPreviewRecipients([]);
        setPreviewRecipientsCount(0);
        setPreviewServerPaginated(false);
        setPreviewRecipientFilter(null);
        setLastPreviewedStep2Selection(null);
        setLastPreviewedExcludedEmails([]);
      } finally {
        setIsPreviewRecipientsLoading(false);
        if (requestId === previewRequestIdRef.current) {
          setOpenRecipientListSignal((prev) => prev + 1);
        }
      }
    },
    [formData.testListMemberIds, loadPreviewRecipients, mapCalculatedRecipientsToRows],
  );

  const handlePreviewRecipientPageChange = useCallback(
    async (
      pageIndex: number,
      pageSize: number,
      sort?: { field: string; order: "asc" | "desc" },
    ) => {
      if (!previewRecipientFilter) {
        return;
      }

      const requestId = previewRequestIdRef.current + 1;
      previewRequestIdRef.current = requestId;
      const sortField = sort?.field || previewRecipientSortRef.current.field;
      const sortOrder = sort?.order || previewRecipientSortRef.current.order;
      previewRecipientSortRef.current = { field: sortField, order: sortOrder };

      setIsPreviewRecipientsLoading(true);
      try {
        const response = await loadPreviewRecipients(
          previewRecipientFilter,
          pageIndex,
          pageSize,
          sortField,
          sortOrder,
          requestId,
        );
        if (!response || requestId !== previewRequestIdRef.current) {
          return;
        }

        setPreviewRecipients(mapCalculatedRecipientsToRows(response.recipients));
        setPreviewRecipientsCount(
          response.pagination?.totalCount ??
          response.count ??
          response.recipients.length,
        );
        setPreviewServerPaginated(response.serverPaginated === true);
      } catch {
        if (requestId !== previewRequestIdRef.current) {
          return;
        }
        setPreviewRecipients([]);
      } finally {
        if (requestId === previewRequestIdRef.current) {
          setIsPreviewRecipientsLoading(false);
        }
      }
    },
    [loadPreviewRecipients, mapCalculatedRecipientsToRows, previewRecipientFilter],
  );

  const isSubmitEnabled = useMemo(() => {
    if (actionData.actionType === "send") {
      return true;
    }
    return !!(actionData.scheduledDate && actionData.scheduledTime);
  }, [actionData.actionType, actionData.scheduledDate, actionData.scheduledTime]);

  const selectedTemplate = useMemo(() => {
    return emailTemplates.find((t) => t.id === formData.emailTemplateId);
  }, [emailTemplates, formData.emailTemplateId]);

  const selectedDistributionListId = useMemo(
    () => formData.recipientListId || "",
    [formData.recipientListId],
  );

  const isSelectedDistributionListSynced = useMemo(
    () =>
      !!formData.recipientListId &&
      selectedDistributionList?.id === formData.recipientListId,
    [formData.recipientListId, selectedDistributionList?.id],
  );

  const selectedRecipientList = useMemo(() => {
    if (!selectedDistributionList?.id || !selectedDistributionList.name) {
      return null;
    }
    return {
      id: selectedDistributionList.id,
      name: selectedDistributionList.name,
    };
  }, [selectedDistributionList]);

  const selectedDistributionListEditorState = useMemo(() => {
    if (!isSelectedDistributionListSynced || !selectedDistributionList?.filter) {
      return parseRecipientFilterForEditor(undefined);
    }
    return parseRecipientFilterForEditor(selectedDistributionList.filter);
  }, [isSelectedDistributionListSynced, selectedDistributionList?.filter]);

  const prefilledOperator = useMemo<Operator | null>(() => {
    if (!isSelectedDistributionListSynced) {
      return null;
    }
    const logic = selectedDistributionListEditorState.selection.logic;
    if (logic === "AND" || logic === "OR") {
      return logic;
    }
    return null;
  }, [isSelectedDistributionListSynced, selectedDistributionListEditorState]);

  const prefilledGroupConnectors = useMemo<Operator[]>(() => {
    if (!isSelectedDistributionListSynced) {
      return [];
    }
    return selectedDistributionListEditorState.selection.groupConnectors;
  }, [isSelectedDistributionListSynced, selectedDistributionListEditorState]);

  const prefilledGroups = useMemo<RecipientConditionGroup[]>(() => {
    if (!isSelectedDistributionListSynced) {
      return [];
    }
    return selectedDistributionListEditorState.selection.groups;
  }, [isSelectedDistributionListSynced, selectedDistributionListEditorState]);

  const handleEnvelopeTemplateUpdated = useCallback(
    (updated: IEmailTemplate) => {
      if (!updated.id) {
        return;
      }

      const previousEmailType =
        emailTemplates.find((template) => template.id === updated.id)
          ?.emailType || "";
      const nextEmailType = String(updated.emailType || "");
      const emailTypeChanged = previousEmailType !== nextEmailType;

      loadedTemplateDetailIdsRef.current.add(updated.id);
      setEmailTemplates((prev) =>
        prev.map((template) => {
          if (template.id !== updated.id) {
            return template;
          }
          const mapped = mapTemplateToOption(updated);
          return {
            ...mapped,
            htmlContent: mapped.htmlContent || template.htmlContent,
            year: mapped.year || template.year,
            emailNumber: mapped.emailNumber || template.emailNumber,
          };
        }),
      );

      // Unsubscribe filtering depends on template emailType — refresh the
      // recipient preview when the envelope type changes in step 3.
      if (!emailTypeChanged) {
        return;
      }
      // Test-list-only sends skip unsubscribe filters and use fixed person ids.
      if (formData.testListMemberIds.length > 0 && !formData.recipientListId) {
        return;
      }

      if (lastPreviewedStep2Selection) {
        void handlePreviewRecipientList(
          lastPreviewedStep2Selection,
          lastPreviewedExcludedEmails,
        );
        return;
      }

      if (selectedDistributionList?.filter) {
        void handlePreviewRecipientList(
          selectedDistributionListEditorState.selection,
          selectedDistributionListEditorState.excludedEmails,
        );
      }
    },
    [
      emailTemplates,
      formData.recipientListId,
      formData.testListMemberIds.length,
      handlePreviewRecipientList,
      lastPreviewedExcludedEmails,
      lastPreviewedStep2Selection,
      selectedDistributionList?.filter,
      selectedDistributionListEditorState.excludedEmails,
      selectedDistributionListEditorState.selection,
    ],
  );

  useEffect(() => {
    if (!selectedDistributionListId) {
      lastAutoPreviewedListIdRef.current = "";
      previewRequestIdRef.current += 1;

      if (formData.testListMemberIds.length > 0) {
        const byId = new Map(
          testListMemberOptions.map((m) => [m.id, m] as const),
        );
        const rows = formData.testListMemberIds
          .map((id) => byId.get(id))
          .filter((m): m is TestListMemberOption => !!m)
          .map((m) => ({
            id: m.id,
            contact: m.name,
            email: m.email,
            firmId: m.firmId,
            firmName: m.firmName,
            location: m.location,
            region: m.region,
          }));
        setPreviewRecipients(rows);
        setPreviewRecipientsCount(rows.length);
        setLastPreviewedStep2Selection(null);
        setLastPreviewedExcludedEmails([]);
        setIsPreviewRecipientsLoading(false);
        setOpenRecipientListSignal((prev) => prev + 1);
        return;
      }

      setPreviewRecipients([]);
      setPreviewRecipientsCount(0);
      setLastPreviewedStep2Selection(null);
      setLastPreviewedExcludedEmails([]);
      if (!isCreatingNewList) {
        if (excludedEmailsRef.current.length > 0) {
          skipNextExcludedEmailsInvalidationRef.current = true;
          setExcludedEmails([]);
        }
      }
      return;
    }

    if (preservePreviewOnNextListSelectionRef.current) {
      preservePreviewOnNextListSelectionRef.current = false;
      return;
    }

    if (
      selectedDistributionListId &&
      !isSelectedDistributionListSynced
    ) {
      return;
    }

    previewRequestIdRef.current += 1;
    setPreviewRecipients([]);
    setPreviewRecipientsCount(0);
    setLastPreviewedStep2Selection(null);
    setLastPreviewedExcludedEmails([]);

    if (
      !areStringArraysEqual(
        excludedEmailsRef.current,
        selectedDistributionListEditorState.excludedEmails,
      )
    ) {
      skipNextExcludedEmailsInvalidationRef.current = true;
      setExcludedEmails(selectedDistributionListEditorState.excludedEmails);
    }

    if (!selectedDistributionList?.filter) {
      return;
    }

    lastAutoPreviewedListIdRef.current = selectedDistributionListId;
    handlePreviewRecipientList(
      selectedDistributionListEditorState.selection,
      selectedDistributionListEditorState.excludedEmails,
    );
  }, [
    selectedDistributionListId,
    isSelectedDistributionListSynced,
    selectedDistributionList,
    selectedDistributionListEditorState,
    isCreatingNewList,
    handlePreviewRecipientList,
    formData.testListMemberIds,
    formData.emailTemplateId,
    testListMemberOptions,
  ]);

  useEffect(() => {
    setLastPreviewedStep2Selection(null);
    setLastPreviewedExcludedEmails([]);
  }, [formData.emailTemplateId]);

  useEffect(() => {
    if (skipNextExcludedEmailsInvalidationRef.current) {
      skipNextExcludedEmailsInvalidationRef.current = false;
      return;
    }

    previewRequestIdRef.current += 1;
    setLastPreviewedStep2Selection(null);
    setLastPreviewedExcludedEmails([]);
  }, [excludedEmails]);

  const hasListSelected = !!formData.recipientListId;
  const hasTestListSelection = formData.testListMemberIds.length > 0;
  const isSkipStep2RecipientListSelected = isSkipStep2RecipientListId(
    formData.recipientListId,
  );
  const showStep2 =
    isCreatingNewList ||
    (hasListSelected && !isSkipStep2RecipientListSelected);
  const hasTemplateSelected = !!formData.emailTemplateId;
  const hasRecipientsForPreview =
    hasListSelected || hasTestListSelection || isCreatingNewList;

  return (
    <div
      className="flex flex-col gap-6"
      style={{ WebkitFontSmoothing: "antialiased", MozOsxFontSmoothing: "grayscale" }}
    >
      <div className="flex justify-end gap-4">
        <LegacyButton
          text="DESIGN EMAIL"
          style="filled"
          color="accent"
          content="text"
          size="small"
          onClick={() => navigate("/email-design")}
        />
        <LegacyButton
          text="PENDING SCHEDULED EMAILS"
          style="filled"
          color="accent"
          content="text"
          size="small"
          onClick={() => navigate("/send-email/scheduled-emails")}
        />
      </div>

      <div className="bg-white border border-gray-200 p-6 lg:p-8">
        <div className="flex flex-col gap-6">
          {currentStep === 1 ? (
            <>
              <SendInformationCard
                formData={formData}
                onFormChange={handleFormChange}
                onCreateNewList={handleCreateNewList}
                emailTemplateOptions={emailTemplates}
                isTemplatesLoading={isTemplatesLoading}
                selectedRecipientList={selectedRecipientList}
                testListMemberOptions={testListMemberOptions}
                isTestListMembersLoading={isTestListMembersLoading}
                onRecipientListTemplateLinked={handleRecipientListTemplateLinked}
              />
              {hasTestListSelection && !showStep2 && (
                <div className="bg-white rounded-lg border border-gray-200 px-6 lg:px-8 py-5 flex flex-col gap-4">
                  <span className="text-sm text-primary">
                    Review the recipient preview below, then continue to schedule or send the
                    email to your Test List.
                  </span>
                  <div className="flex justify-end">
                    <LegacyButton
                      text="PROCEED TO STEP 3"
                      icon={<ArrowRightIcon />}
                      style="filled"
                      color="accent"
                      content="textIcon"
                      size="small"
                      onClick={handleProceedToStep3FromTestList}
                      disabled={
                        !hasTemplateSelected ||
                        !hasTestListSelection ||
                        isTestListMembersLoading ||
                        isSelectedTemplateLoading
                      }
                    />
                  </div>
                </div>
              )}
              {isSkipStep2RecipientListSelected && !showStep2 && (
                <div className="bg-white rounded-lg border border-gray-200 px-6 lg:px-8 py-5 flex flex-col gap-4">
                  <span className="text-sm text-primary">
                    Review the recipient list preview below, then continue to schedule or
                    send the email to all active members.
                  </span>
                  <div className="flex justify-end">
                    <LegacyButton
                      text="PROCEED TO STEP 3"
                      icon={<ArrowRightIcon />}
                      style="filled"
                      color="accent"
                      content="textIcon"
                      size="small"
                      onClick={handleProceedToStep3FromBuiltinList}
                      disabled={
                        !hasTemplateSelected ||
                        isSelectedTemplateLoading ||
                        isPreviewRecipientsLoading ||
                        previewRecipientsCount <= 0
                      }
                    />
                  </div>
                </div>
              )}
              {showStep2 && (
                <RecipientListCard
                  key={`recipient-list-card-${isCreatingNewList
                    ? "new"
                    : selectedDistributionListId || "empty"
                    }`}
                  onProceedToNextStep={handleProceedToStep3}
                  excludedEmails={excludedEmails}
                  onRemoveExclusion={handleRemoveExclusion}
                  hasTemplateSelected={hasTemplateSelected}
                  prefilledOperator={prefilledOperator}
                  prefilledGroupConnectors={prefilledGroupConnectors}
                  prefilledGroups={prefilledGroups}
                  prefillKey={selectedDistributionListId}
                  isPrefillLoading={
                    !!selectedDistributionListId &&
                    (isSelectedDistributionListLoading ||
                      !isSelectedDistributionListSynced)
                  }
                  onPreviewRecipientList={handlePreviewRecipientList}
                  lastPreviewedSelection={lastPreviewedStep2Selection}
                  lastPreviewedExcludedEmails={lastPreviewedExcludedEmails}
                  isPreviewLoading={isPreviewRecipientsLoading}
                  previewCount={previewRecipientsCount}
                  isProceedLoading={isPersistingDistributionList}
                />
              )}
            </>
          ) : (
            <>
              <ActionCard
                formData={actionData}
                onFormChange={handleActionChange}
              />
              <EnvelopeInformationCard
                selectedTemplate={selectedTemplate || null}
                onTemplateUpdated={handleEnvelopeTemplateUpdated}
                onEditingChange={setIsEnvelopeEditing}
              />
              <div className="flex justify-end gap-4">
                <LegacyButton
                  text="GO BACK"
                  icon={<UpdateIcon />}
                  style="outlined"
                  color="accent"
                  content="textIcon"
                  size="small"
                  onClick={handleBackToStep1}
                />
                <LegacyButton
                  text="SUBMIT"
                  icon={<ArrowRightIcon />}
                  style="filled"
                  color="accent"
                  content="textIcon"
                  size="small"
                  onClick={handleSubmitClick}
                  disabled={!isSubmitEnabled || isEnvelopeEditing}
                />
              </div>
            </>
          )}
          <PreviewSection
            hasTemplateSelected={hasTemplateSelected}
            hasListSelected={hasRecipientsForPreview}
            selectedTemplate={selectedTemplate || null}
            isTemplatePreviewLoading={isSelectedTemplateLoading}
            recipientRows={previewRecipients}
            isRecipientListLoading={isPreviewRecipientsLoading}
            openRecipientListSignal={openRecipientListSignal}
            excludedEmails={excludedEmails}
            onExcludeEmail={handleExcludeEmail}
            onExcludeEmails={handleExcludeEmails}
            serverPaginated={previewServerPaginated}
            totalRecipientCount={previewRecipientsCount}
            onRecipientPageChange={handlePreviewRecipientPageChange}
          />
        </div>
      </div>

      <SendEmailConfirmationModal
        isOpen={showConfirmationModal}
        emailNumber={selectedTemplate?.emailNumber || undefined}
        templateName={selectedTemplate?.name || ""}
        isScheduled={actionData.actionType === "schedule"}
        scheduledDate={actionData.scheduledDate}
        scheduledTime={actionData.scheduledTime}
        isSubmitting={isSubmittingEmailSend}
        onCancel={handleConfirmCancel}
        onConfirm={handleConfirmSend}
      />
      <RecipientListCreatedModal
        isOpen={showListCreatedModal}
        listName={createdListName}
        onContinue={handleContinueAfterListCreated}
      />
    </div>
  );
}
